// User sampler: play the user's own audio samples as an instrument.
//   pitched → one sample pitch-shifted across the keyboard (root note).
//   kit     → each sample mapped to a specific note (drum/one-shot pads).
// Samples are stored as project assets (assetId), so they persist with the
// project; the AudioBuffers are supplied by the caller (engine/render). Pure
// Web Audio, works live and in offline render.
import { Instrument } from "./instrument";

export interface SamplerSample { assetId: string; name: string; note?: number; }
export interface SamplerDef { type: "sampler"; mode: "pitched" | "kit"; rootMidi?: number; samples: SamplerSample[]; }

export class SamplerInstrument implements Instrument {
  output: GainNode;
  private live = new Map<number, { src: AudioBufferSourceNode; g: GainNode }>();
  private active = new Set<AudioBufferSourceNode>();

  constructor(private ctx: BaseAudioContext, private def: SamplerDef, private buffers: Map<string, AudioBuffer>) {
    this.output = ctx.createGain();
  }

  private pick(midi: number): { buf: AudioBuffer; rate: number } | null {
    if (this.def.mode === "kit") {
      const s = this.def.samples.find((x) => x.note === midi);
      const buf = s && this.buffers.get(s.assetId);
      return buf ? { buf, rate: 1 } : null;
    }
    const s = this.def.samples[0];
    const buf = s && this.buffers.get(s.assetId);
    if (!buf) return null;
    const root = this.def.rootMidi ?? 60;
    return { buf, rate: Math.pow(2, (midi - root) / 12) };
  }

  private voice(midi: number, vel: number, when: number, dur?: number) {
    const p = this.pick(midi);
    if (!p) return null;
    const src = this.ctx.createBufferSource();
    src.buffer = p.buf; src.playbackRate.value = p.rate;
    const g = this.ctx.createGain();
    const peak = Math.max(0.001, vel);
    g.gain.setValueAtTime(0.001, when);
    g.gain.linearRampToValueAtTime(peak, when + 0.005);
    src.connect(g).connect(this.output);
    this.active.add(src); src.onended = () => this.active.delete(src);
    src.start(when);
    if (dur != null) {
      if (this.def.mode === "pitched") { // held for `dur`, then release
        const rel = 0.08;
        g.gain.setValueAtTime(peak, when + dur);
        g.gain.linearRampToValueAtTime(0.001, when + dur + rel);
        try { src.stop(when + dur + rel + 0.02); } catch {}
      } else { // kit one-shot: ring for the sample length
        try { src.stop(when + p.buf.duration / p.rate + 0.05); } catch {}
      }
    }
    return { src, g };
  }

  noteOn(midi: number, vel = 0.8) {
    this.noteOff(midi, true);
    const v = this.voice(midi, vel, this.ctx.currentTime);
    if (v && this.def.mode === "pitched") this.live.set(midi, v); // held → released on noteOff
  }
  noteOff(midi: number, immediate = false) {
    const v = this.live.get(midi);
    if (!v) return;
    this.live.delete(midi);
    const now = this.ctx.currentTime, rel = immediate ? 0.01 : 0.08;
    try { v.g.gain.cancelScheduledValues(now); v.g.gain.setValueAtTime(Math.max(0.001, v.g.gain.value), now); v.g.gain.linearRampToValueAtTime(0.001, now + rel); } catch {}
    try { v.src.stop(now + rel + 0.02); } catch {}
  }
  scheduleNote(midi: number, vel: number, start: number, dur: number) { this.voice(midi, vel, start, dur); }
  allNotesOff() { for (const s of this.active) { try { s.stop(); } catch {} } this.active.clear(); this.live.clear(); }
  dispose() { this.allNotesOff(); try { this.output.disconnect(); } catch {} }
}
